import { useEffect, useRef, useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { ErrorBoundary, MapContainer, ControlPanel, POIHistory, RouteStats, POIDetailPanel, POIList } from './components/organisms'
import { MainLayout } from './components/templates'
import { POIUpdateNotification, LanguageSelector, DistanceDisplay, MapTypeSelector, PinLocationButton, FeatureCard } from './components/molecules'
import { Card, Button, Icon } from './components/atoms'
import { useAppStore } from './store/appStore'
import { useGeolocation } from './hooks/useGeolocation'
import { useWakeLock } from './hooks/useWakeLock'
import { usePOIDetection } from './hooks/usePOIDetection'
import { useSpeech } from './hooks/useSpeech'
import { useRoute } from './hooks/useRoute'
import { usePOIDetectionAtLocation } from './hooks/usePOIDetectionAtLocation'
import { RADIO_MIN_INTERVAL_MS } from './utils/constants'
import { now, haversineMeters } from './utils/geo'
import { chosenLang } from './utils/text'
import type { Position, POICandidate } from './types'
import type L from 'leaflet'

function App() {
  const store = useAppStore()
  const { speak, cancel } = useSpeech()
  const { handlePositionUpdate, narrateOnce, narratePOI } = usePOIDetection()
  const { planRoute, clearRoute, setMapRef: setRouteMapRef } = useRoute()
  const { detectPOIsAtLocation: detectPOIsAtPinnedLocation } = usePOIDetectionAtLocation()
  const [logs, setLogs] = useState<string[]>([])
  const [showHistory, setShowHistory] = useState(false)
  const [showStats, setShowStats] = useState(false)
  const [lastNotifiedPOI, setLastNotifiedPOI] = useState<string | null>(null)
  const lastSpokenStartedAtRef = useRef(0)
  const radioIntervalRef = useRef<number | null>(null)
  const mapRefForPin = useRef<L.Map | null>(null)

  const handlePositionUpdateWithRoute = (
    position: Position,
    accuracy: number
  ) => {
    try {
      // Always update position and route, but only detect POIs when appropriate
      handlePositionUpdate(position, accuracy)
      if (store.isTracking) {
        store.addRoutePosition(position, accuracy)
      }
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err))
      log(`Error updating position: ${error.message}`)
      console.error('Error in handlePositionUpdateWithRoute:', error)
    }
  }

  const { position, error, accuracy, refresh: refreshLocation } = useGeolocation(
    store.isTracking,
    handlePositionUpdateWithRoute,
    store.settings.autoUpdate
  )

  // Calculate distance to current POI
  const distanceToPOI =
    position && store.poiPosition
      ? haversineMeters(
          position.lat,
          position.lon,
          store.poiPosition.lat,
          store.poiPosition.lon
        )
      : null

  useWakeLock(store.isTracking)

  // Logging function
  const log = (message: string) => {
    const timestamp = new Date().toLocaleTimeString()
    setLogs((prev) => [`${timestamp}  ${message}`, ...prev.slice(0, 99)])
  }

  // Handle start
  const handleStart = () => {
    try {
      if (!navigator.geolocation) {
        store.setStatus('Geen geolocation in deze browser.')
        log('Geolocation not supported')
        return
      }

      // Clear pinned locations and POIs when starting tracking
      // This ensures the map will focus on GPS position
      store.clearPinnedLocations()
      store.setNearbyPOIs([])
      
      // Save current map view before starting tracking
      // so pin location can use the user's current view later
      if (mapRefForPin.current) {
        const center = mapRefForPin.current.getCenter()
        const zoom = mapRefForPin.current.getZoom()
        store.setSavedMapView({ center: [center.lat, center.lng], zoom })
      }
      
      store.setIsTracking(true)
      store.setStatus('Start… (GPS)')
      store.setRouteStartTime(now())
      store.clearRoute()
      log('Start watchPosition')
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err))
      store.setStatus('Fout bij starten')
      log(`Error starting: ${error.message}`)
      console.error('Error in handleStart:', error)
    }
  }

  // Handle stop
  const handleStop = () => {
    try {
      cancel()
      store.setIsTracking(false)
      store.setStatus('Gestopt.')
      store.setMapMode('north')
      store.setRouteStartTime(null)
      log('Stopped')
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err))
      log(`Error stopping: ${error.message}`)
      console.error('Error in handleStop:', error)
    }
  }

  // Handle test speech
  const handleTest = () => {
    try {
      speak('Test. De stem werkt.', chosenLang())
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err))
      store.setStatus('Fout bij test stem')
      log(`Error testing speech: ${error.message}`)
      console.error('Error in handleTest:', error)
    }
  }

  // Radio mode interval
  useEffect(() => {
    if (!store.isTracking || !store.settings.radio) {
      if (radioIntervalRef.current) {
        clearInterval(radioIntervalRef.current)
        radioIntervalRef.current = null
      }
      return
    }

    radioIntervalRef.current = window.setInterval(() => {
      const t = now()
      if (t - lastSpokenStartedAtRef.current >= RADIO_MIN_INTERVAL_MS) {
        narrateOnce()
      }
    }, 900)

    return () => {
      if (radioIntervalRef.current) {
        clearInterval(radioIntervalRef.current)
        radioIntervalRef.current = null
      }
    }
  }, [store.isTracking, store.settings.radio, narrateOnce])

  // Handle geolocation errors
  useEffect(() => {
    if (error) {
      store.setStatus('Locatie fout')
      log(`Geolocation error: ${error.code} ${error.message || 'unknown'}`)
    }
    // Only depend on error, not the entire store object
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error])


  return (
    <ErrorBoundary>
      <Helmet>
        <html lang="nl" />
        <title>PeriScope - Location-Based Audio Narration</title>
        <meta
          name="description"
          content="Discover points of interest around you with real-time GPS tracking and audio narration. Explore Wikipedia, OpenStreetMap, and Wikidata locations as you travel."
        />
        <meta name="keywords" content="GPS, location, narration, points of interest, travel, exploration, audio guide, Wikipedia, OpenStreetMap" />
        <meta name="author" content="PeriScope" />
        <meta name="robots" content="index, follow" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://periscope.app/" />
        <meta property="og:title" content="PeriScope - Location-Based Audio Narration" />
        <meta property="og:description" content="Discover points of interest around you with real-time GPS tracking and audio narration." />
        <meta property="og:image" content="/og-image.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:locale" content="en_US" />
        <meta property="og:site_name" content="PeriScope" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content="https://periscope.app/" />
        <meta name="twitter:title" content="PeriScope - Location-Based Audio Narration" />
        <meta name="twitter:description" content="Discover points of interest around you with real-time GPS tracking and audio narration." />
        <meta name="twitter:image" content="/og-image.png" />
        
        {/* Additional SEO */}
        <link rel="canonical" href="https://periscope.app/" />
        <meta name="geo.region" content="NL" />
        <meta name="geo.placename" content="Netherlands" />
        <meta name="mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent" />
        <meta name="apple-mobile-web-app-title" content="PeriScope" />
        
        {/* Preconnect to external domains for performance */}
        <link rel="preconnect" href="https://overpass-api.de" />
        <link rel="preconnect" href="https://overpass.kumi.systems" />
        <link rel="preconnect" href="https://www.wikidata.org" />
        <link rel="dns-prefetch" href="https://*.wikipedia.org" />
        <link rel="dns-prefetch" href="https://*.tile.openstreetmap.org" />
        
        {/* Structured Data - WebApplication */}
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'WebApplication',
            name: 'PeriScope',
            description:
              'Location-based audio narration app that discovers and narrates points of interest using GPS tracking',
            applicationCategory: 'TravelApplication',
            operatingSystem: 'Web',
            browserRequirements: 'Requires JavaScript. Requires HTML5.',
            offers: {
              '@type': 'Offer',
              price: '0',
              priceCurrency: 'USD',
            },
            featureList: [
              'Real-time GPS tracking',
              'Audio narration of points of interest',
              'Wikipedia integration',
              'OpenStreetMap integration',
              'Wikidata integration',
              'Google Places integration',
              'POI history tracking',
              'Route statistics',
              'Multi-language support',
            ],
            inLanguage: ['nl', 'en', 'de', 'fr', 'es', 'it', 'pt', 'sv', 'da', 'no'],
            accessibilityFeature: [
              'screenReaderSupport',
              'highContrastDisplay',
              'keyboardNavigation',
            ],
            accessibilityHazard: 'none',
          })}
        </script>
        {/* Structured Data - SoftwareApplication */}
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'SoftwareApplication',
            name: 'PeriScope',
            operatingSystem: 'Web',
            applicationCategory: 'TravelApplication',
            offers: {
              '@type': 'Offer',
              price: '0',
            },
            aggregateRating: {
              '@type': 'AggregateRating',
              ratingValue: '4.5',
              ratingCount: '1',
            },
          })}
        </script>
      </Helmet>

      <MainLayout>
        <div className="grid grid-cols-1 lg:grid-cols-[1.2fr_0.8fr] gap-3">
          <section className="space-y-2">
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-2.5">
              <MapTypeSelector
                currentType={store.mapType}
                onChange={store.setMapType}
              />
              <PinLocationButton
                onClick={() => {
                  // If there's already a pin, clear it first
                  if (store.pinnedLocations.length > 0) {
                    store.clearPinnedLocations()
                    store.setNearbyPOIs([])
                  }
                  // Toggle pin mode - cursor becomes crosshair for user to click on map
                  store.setPinMode(true)
                }}
              />
            </div>
            <MapContainer
              position={position}
              heading={store.heading}
              mapMode={store.mapMode}
              follow={store.settings.autoUpdate}
              accuracy={accuracy}
              isTracking={store.isTracking}
              onMapModeToggle={() => {
                const newMode = store.mapMode === 'north' ? 'follow' : 'north'
                store.setMapMode(newMode)
              }}
              onZoomIn={() => {}}
              onZoomOut={() => {}}
              poiPosition={store.poiPosition}
              nearbyPOIs={(store.settings.showPOIs ? store.nearbyPOIs : []).map(poi => ({
                lat: poi.lat,
                lon: poi.lon,
                title: poi.title,
                key: poi.key,
              }))}
              mapType={store.mapType}
              pinnedLocations={store.pinnedLocations}
              compassLocked={store.compassLocked}
              onCompassLockToggle={() => store.setCompassLocked(!store.compassLocked)}
              manualRotation={store.manualRotation}
              onManualRotation={(angle: number) => store.setManualRotation(angle)}
              onPOIMarkerClick={(poi: { key: string; lat: number; lon: number; title: string }) => {
                // Handle POI marker click from map
                const fullPOI = store.nearbyPOIs.find(p => p.key === poi.key)
                if (fullPOI) {
                  store.setSelectedPOI(fullPOI)
                  store.setPoiPosition({
                    lat: fullPOI.lat,
                    lon: fullPOI.lon,
                    title: fullPOI.title,
                  })
                }
              }}
              onMapClick={async (lat: number, lon: number) => {
                try {
                  if (store.pinMode) {
                    // Clear previous pins and POIs before adding a new one
                    store.clearPinnedLocations()
                    store.setNearbyPOIs([])
                    
                    // Pin mode: add location and detect POIs, then exit pin mode
                    store.addPinnedLocation(lat, lon)
                    store.setPinMode(false) // Exit pin mode after pinning
                    
                    // Detect POIs at pinned location (don't override GPS position)
                    await detectPOIsAtPinnedLocation(lat, lon)
                  }
                  // Normal click: can be used for other purposes in the future
                } catch (err) {
                  const error = err instanceof Error ? err : new Error(String(err))
                  store.setStatus('Fout bij klikken op kaart')
                  log(`Error on map click: ${error.message}`)
                  console.error('Error in onMapClick:', error)
                }
              }}
              pinMode={store.pinMode}
              onMapReady={(map: L.Map) => {
                setRouteMapRef(map)
                mapRefForPin.current = map
              }}
            />

            {/* Nearby Points of Interest */}
            <FeatureCard
              title="Nearby Points of Interest"
              icon={<Icon name="location" className="w-5 h-5" />}
            >
              <POIList
                pois={store.nearbyPOIs}
                userPosition={position}
                onPOISelect={(poi: POICandidate) => {
                  store.setSelectedPOI(poi)
                  store.setPoiPosition({
                    lat: poi.lat,
                    lon: poi.lon,
                    title: poi.title,
                  })
                }}
                onRoute={(poi: POICandidate) => {
                  planRoute(poi)
                  store.setSelectedPOI(poi)
                }}
                onNarrate={narratePOI}
                onStartTracking={handleStart}
                isTracking={store.isTracking}
                onPinLocation={() => {
                  // If there's already a pin, clear it first
                  if (store.pinnedLocations.length > 0) {
                    store.clearPinnedLocations()
                    store.setNearbyPOIs([])
                  }
                  // Enable pin mode for user to click on map
                  store.setPinMode(true)
                }}
                narratingPOI={store.narratingPOI}
                narrationError={store.narrationError}
                isLoading={store.isFetchingPOIs}
              />
            </FeatureCard>
          </section>

          {/* Right Column: Control Panel */}
          <ControlPanel
            state={{
              status: store.status,
              lastLocation: store.lastLocation,
              heading: store.heading,
              lastPOI: store.lastPOI,
              lastSpoken: store.lastSpoken,
              isTracking: store.isTracking,
              settings: store.settings,
              position: store.position,
              mapMode: store.mapMode,
              mapType: store.mapType,
              compassLocked: store.compassLocked,
              manualRotation: store.manualRotation,
            }}
            settings={store.settings}
            onStart={handleStart}
            onStop={handleStop}
            onTest={handleTest}
            onRefresh={refreshLocation}
            onSettingsChange={store.updateSettings}
            onClearPOIs={() => {
              store.setNearbyPOIs([])
              store.setSelectedPOI(null)
              store.setPoiPosition(null)
              log('Cleared all POIs from map')
            }}
            onRefreshPOIs={() => {
              if (position) {
                handlePositionUpdate(position, accuracy || 0)
                log('Refreshing POIs at current location')
              }
            }}
            onClearPinnedLocations={() => {
              store.clearPinnedLocations()
              log('Cleared all pinned locations')
            }}
            poiCount={store.nearbyPOIs.length}
            pinnedCount={store.pinnedLocations.length}
          />
        </div>

        {/* Features Section */}
        <section className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {/* Left Column */}
          <div className="space-y-3">
            {/* Language & Distance */}
            <Card>
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2">
                  <Icon name="info" className="w-4 h-4 text-text-muted" />
                  <span className="text-sm font-medium text-text-muted">Language:</span>
                  <LanguageSelector
                    currentLang={store.language}
                    onLanguageChange={store.setLanguage}
                  />
                </div>
                {distanceToPOI != null && (
                  <div className="flex items-center gap-2 text-sm">
                    <Icon name="location" className="w-4 h-4 text-primary" />
                    <span className="text-text-muted">Distance:</span>
                    <span className="font-medium text-text">
                      <DistanceDisplay distance={distanceToPOI} />
                    </span>
                  </div>
                )}
              </div>
            </Card>

            {/* Pinned Locations */}
            {store.pinnedLocations.length > 0 && (
              <FeatureCard
                title="Pinned Locations"
                icon={<Icon name="location" className="w-5 h-5" />}
              >
                <div className="space-y-2">
                  {store.pinnedLocations.map((pin) => (
                    <div
                      key={pin.id}
                      className="p-2 bg-background-elevated rounded-lg border border-border flex items-center justify-between"
                    >
                      <div className="text-sm">
                        <div className="font-medium">
                          {pin.lat.toFixed(5)}, {pin.lon.toFixed(5)}
                        </div>
                        <div className="text-xs text-text-muted">
                          {new Date(pin.timestamp).toLocaleTimeString()}
                        </div>
                      </div>
                      <Button
                        onClick={() => {
                          store.removePinnedLocation(pin.id)
                        }}
                        variant="secondary"
                        size="sm"
                      >
                        Remove
                      </Button>
                    </div>
                  ))}
                  <Button
                    onClick={() => {
                      store.clearPinnedLocations()
                    }}
                    variant="secondary"
                    size="sm"
                    className="w-full"
                  >
                    Clear All
                  </Button>
                </div>
              </FeatureCard>
            )}


            {/* Route Stats */}
            {showStats && store.isTracking && (
              <FeatureCard
                title="Route Statistics"
                icon={<Icon name="location" className="w-5 h-5" />}
              >
                <RouteStats
                  positions={store.routePositions}
                  startTime={store.routeStartTime}
                />
              </FeatureCard>
            )}

            {/* POI History */}
            {showHistory && (
              <FeatureCard
                title="POI History"
                icon={<Icon name="info" className="w-5 h-5" />}
              >
                <POIHistory
                  history={store.poiHistory}
                  onClear={store.clearPOIHistory}
                />
              </FeatureCard>
            )}
          </div>

          {/* Right Column: Quick Actions */}
          <aside className="space-y-3">
            <Card>
              <h3 className="font-semibold mb-3 text-base">Quick Actions</h3>
              <div className="grid grid-cols-2 gap-2">
                <Button
                  onClick={() => setShowHistory(!showHistory)}
                  variant={showHistory ? 'primary' : 'secondary'}
                  className="text-sm py-2.5"
                >
                  {showHistory ? 'Hide' : 'Show'} History
                </Button>
                <Button
                  onClick={() => setShowStats(!showStats)}
                  variant={showStats ? 'primary' : 'secondary'}
                  className="text-sm py-2.5"
                >
                  {showStats ? 'Hide' : 'Show'} Stats
                </Button>
                {store.selectedPOI && (
                  <>
                    <Button
                      onClick={() => {
                        store.setSelectedPOI(null)
                        clearRoute()
                      }}
                      variant="secondary"
                      className="text-sm py-2.5 col-span-2"
                    >
                      Clear Route
                    </Button>
                  </>
                )}
              </div>
            </Card>

            {/* Debug Log - Collapsible */}
            <Card>
              <details className="group">
                <summary className="cursor-pointer font-semibold text-sm text-text-muted hover:text-text transition-colors list-none">
                  <div className="flex items-center justify-between">
                    <span>Debug Log</span>
                    <svg
                      className="w-4 h-4 transition-transform group-open:rotate-180"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </summary>
                <div className="mt-3 pt-3 border-t border-border">
                  <div className="font-mono text-xs leading-relaxed whitespace-pre-wrap max-h-64 overflow-y-auto text-text-muted">
                    {logs.length > 0 ? logs.join('\n') : 'No logs yet'}
                  </div>
                </div>
              </details>
            </Card>
          </aside>
        </section>

        {/* POI Update Notification */}
        {store.lastPOI && store.lastPOI !== '-' && store.lastPOI === lastNotifiedPOI && (
          <POIUpdateNotification
            poi={store.nearbyPOIs.find((p) => p.title === store.lastPOI) || null}
            onDismiss={() => setLastNotifiedPOI(null)}
          />
        )}

        {/* POI Detail Panel */}
        {store.selectedPOI && (
          <POIDetailPanel
            poi={store.selectedPOI}
            userPosition={position}
            onClose={() => {
              store.setSelectedPOI(null)
              clearRoute()
            }}
            onRoute={(poi: POICandidate) => {
              planRoute(poi)
            }}
            onNarrate={narratePOI}
            language={store.language}
            narratingPOI={store.narratingPOI}
          />
        )}
      </MainLayout>
    </ErrorBoundary>
  )
}

export default App
